import { Component, Input, OnChanges, ChangeDetectionStrategy } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { IconService, IconType } from '../../services/icon.service';

/**
 * Maps the persisted category icon key (from `category.icon`) to the shared
 * IconService registry entry. The keys are the values stored in the DB /
 * validated by the backend (`CATEGORY_ICON_KEYS`).
 */
const KEY_TO_ICON_TYPE: Readonly<Record<string, IconType>> = {
  tennis: IconType.TENNIS,
  padel: IconType.PADEL,
  court: IconType.COURT,
};

/** Fallback icon key when a category has no/an unknown icon. */
const FALLBACK_ICON_KEY = 'court';

/**
 * Pickable icon keys with German labels (e.g. for the admin icon picker).
 * The `court` key stays valid as the render fallback but is not pickable.
 */
export const CATEGORY_ICONS: ReadonlyArray<{ key: string; label: string }> = [
  { key: 'tennis', label: 'Tennis' },
  { key: 'padel', label: 'Padel' },
];

/**
 * Renders a sport icon for a court category as an inline stroke SVG.
 * Thin domain wrapper around the shared IconService registry: it maps the
 * persisted `category.icon` key, falls back to the generic court icon for
 * null/unknown keys, and renders at the lighter stroke weight (1.7) the
 * category design uses. Color follows the CSS `currentColor` of the host,
 * size via the `size` input.
 */
@Component({
  selector: 'app-category-icon',
  standalone: true,
  template: `
    <svg
      [attr.width]="size"
      [attr.height]="size"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      stroke-width="1.7"
      stroke-linecap="round"
      stroke-linejoin="round"
      aria-hidden="true"
      [innerHTML]="iconMarkup"></svg>
  `,
  changeDetection: ChangeDetectionStrategy.Eager,
  styles: [
    `
      :host {
        display: inline-flex;
        line-height: 0;
        flex-shrink: 0;
      }
    `,
  ],
})
export class CategoryIconComponent implements OnChanges {
  /** Sport icon key (see CATEGORY_ICONS); null/unknown ⇒ generic court icon */
  @Input() icon: string | null | undefined;

  /** Rendered width/height in px */
  @Input() size = 20;

  iconMarkup: SafeHtml;

  constructor(
    private sanitizer: DomSanitizer,
    private iconService: IconService,
  ) {
    this.iconMarkup = this.resolveMarkup(this.icon);
  }

  ngOnChanges(): void {
    this.iconMarkup = this.resolveMarkup(this.icon);
  }

  private resolveMarkup(icon: string | null | undefined): SafeHtml {
    const iconType =
      (icon && KEY_TO_ICON_TYPE[icon]) || KEY_TO_ICON_TYPE[FALLBACK_ICON_KEY];
    // Registry markup is static app-owned SVG — safe to bypass sanitization.
    const markup = this.iconService.getIconDefinition(iconType)?.paths ?? '';
    return this.sanitizer.bypassSecurityTrustHtml(markup);
  }
}
